import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';

function EventMetadataDefinitionList() {
  const { 
    getEventMetadataDefinitions, 
    deleteEventMetadataDefinition, 
    isLoading, 
    generateCacheKey 
  } = useData();
  
  const [definitions, setDefinitions] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const cacheKey = generateCacheKey('event-metadata-definitions');
  const loading = isLoading(cacheKey);

  const loadMetadataDefinitions = useCallback(async () => {
    try {
      setError(null);
      const data = await getEventMetadataDefinitions();
      console.log('Event metadata definitions loaded from DataContext:', data);
      setDefinitions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading metadata definitions:', error);
      setError('Failed to load metadata definitions. Please try again.');
    }
  }, [getEventMetadataDefinitions]);

  useEffect(() => {
    loadMetadataDefinitions();
  }, [loadMetadataDefinitions]);

  const handleDelete = async (id, fieldName) => {
    if (!window.confirm(`Are you sure you want to delete the metadata definition "${fieldName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setError(null);
      await deleteEventMetadataDefinition(id);
      // Refresh the list
      await loadMetadataDefinitions();
      console.log('Event metadata definition deleted successfully');
    } catch (error) {
      console.error('Error deleting metadata definition:', error);
      setError('Failed to delete metadata definition. Please try again.');
    }
  };

  const filteredDefinitions = definitions.filter(definition =>
    definition.fieldName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    definition.fieldType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    definition.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading event metadata definitions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page-content">
      <div className="settings-page-header">
        <h2>Event Metadata Definitions</h2>
        <Link to="/settings/event-metadata-definitions/create">
          <button type="button">Create New Definition</button>
        </Link>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search metadata definitions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {filteredDefinitions.length === 0 ? (
        <div className="empty-state">
          <h3>No definitions found</h3>
          <p>{searchTerm ? 'No metadata definitions match your search.' : 'No metadata definitions found. Create your first one!'}</p>
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Field Name</th>
              <th>Field Type</th>
              <th>Description</th>
              <th>Required</th>
              <th>Display Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDefinitions.map((definition) => (
              <tr key={definition.id}>
                <td data-label="Field Name">
                  <strong>{definition.fieldName || 'Unnamed Field'}</strong>
                  {definition.defaultValue && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                      Default: {definition.defaultValue}
                    </div>
                  )}
                </td>
                <td data-label="Field Type">
                  <span className="field-type-badge" style={{
                    backgroundColor: getFieldTypeColor(definition.fieldType),
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>
                    {getFieldTypeLabel(definition.fieldType)}
                  </span>
                </td>
                <td data-label="Description">{definition.description || 'No description'}</td>
                <td data-label="Required">
                  <span className={`status-badge ${definition.isRequired ? 'status-active' : 'status-inactive'}`}>
                    {definition.isRequired ? 'Yes' : 'No'}
                  </span>
                </td>
                <td data-label="Display Order">{definition.displayOrder || 1}</td>
                <td data-label="Actions">
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Link to={`/settings/event-metadata-definitions/${definition.id}`}>
                      Edit
                    </Link>
                    <button onClick={() => handleDelete(definition.id, definition.fieldName)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="info-panel">
        <h4>About Event Metadata Definitions</h4>
        <p>
          Event metadata definitions allow you to create custom fields for your events. These fields help you capture 
          additional information about your learning events such as location, attendance, difficulty rating, or any other 
          details that are important for tracking your educational activities.
        </p>
      </div>
    </div>
  );
}

// Helper functions for field type styling
function getFieldTypeColor(fieldType) {
  const colors = {
    'TEXT': '#6c757d',
    'NUMBER': '#007bff',
    'DATE': '#28a745',
    'DATETIME': '#17a2b8',
    'BOOLEAN': '#ffc107',
    'SELECT': '#6f42c1',
    'MULTISELECT': '#e83e8c',
    'TEXTAREA': '#fd7e14',
    'URL': '#20c997',
    'EMAIL': '#dc3545',
    'TIME': '#6610f2',
    'DURATION': '#495057',
    'RATING': '#f0ad4e'
  };
  return colors[fieldType] || '#6c757d';
}

function getFieldTypeLabel(fieldType) {
  const labels = {
    'TEXT': 'Text',
    'NUMBER': 'Number',
    'DATE': 'Date',
    'DATETIME': 'Date & Time',
    'BOOLEAN': 'Boolean',
    'SELECT': 'Select',
    'MULTISELECT': 'Multi-Select',
    'TEXTAREA': 'Text Area',
    'URL': 'URL',
    'EMAIL': 'Email',
    'TIME': 'Time',
    'DURATION': 'Duration',
    'RATING': 'Rating'
  };
  return labels[fieldType] || fieldType;
}

export default EventMetadataDefinitionList;